import React, {useMemo, useId, useState, useEffect, useRef, useCallback} from 'react'
import {
    Text,
    Box,

    /* Hooks */
    useMultiStyleConfig,
    useTheme
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import useCarouselBleed from '../../../../hooks/use-carousel-bleed'

/* Utils */
import {renderComponentsSwitch} from '../../utils/contentRenderer'
import {generateStyles, getHideStyles} from '../../utils/utils'
import {calculateGridImageSizes, calculateGridImageWidths} from '../../../../utils/image-cdn-utils'

/* Icons */
import {ChevronLeftIcon, ChevronRightIcon} from '../../../icons/index'

/* Constants */
const GAP_SIZE = 8 // Gap between carousel items in pixels

const CategoryCarouselBlock = ({
    Categories,
    CarouselTitle,
    CarouselTitleDisplayStyleDesktop,
    CarouselTitleDisplayStyleMobile,
    CarouselTitleDisplayStyleTablet,
    CategoryCarouselBlockCTATextStyle,
    CategoryCarouselBlockCarouselTitleStyle,
    hideTitle = false,
    customWrapperStyling,
    CtaPlacement,
    CategoryCarouselSize,
    DesktopTileNumber,
    TabletTileNumber,
    TileNumber,
    TextColor,
    Underline,
    HideOnDevices,
    hydrationCount = 0, // From withViewportHydration HOC - triggers re-measurement
    ...props
}) => {
    const theme = useTheme()
    const lgBreakpoint = parseFloat(theme.breakpoints.lg) * 16
    const mdBreakpoint = parseFloat(theme.breakpoints.md) * 16

    // Carousel slide counts per breakpoint
    const effectiveMobileSlides = TileNumber || 2.5
    const effectiveTabletSlides = TabletTileNumber || 4.5
    const effectiveDesktopSlides = DesktopTileNumber || 8

    // Ref for CSS scroll container (used for arrow navigation)
    const scrollContainerRef = useRef(null)

    // Track if we need desktop arrows (only when more items than visible)
    const needsDesktopArrows = (Categories?.length || 0) > effectiveDesktopSlides

    // State for arrow visibility based on scroll position
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(true)

    const {
        containerRef,
        shouldBleedMobile,
        shouldBleedTablet,
        shouldBleedDesktop,
        mobileOffset,
        tabletOffset,
        desktopOffset,
        bleedMargin,
        isAlreadyFullViewport
    } = useCarouselBleed({
        itemCount: Categories?.length || 0,
        slidesToShowMobile: effectiveMobileSlides,
        slidesToShowTablet: effectiveTabletSlides,
        slidesToShowDesktop: effectiveDesktopSlides,
        remeasureTrigger: hydrationCount // Re-measure when hydration occurs
    })

    const styles = useMultiStyleConfig('CategoryCarouselBlock', {
        CarouselTitleDisplayStyleDesktop: CarouselTitleDisplayStyleDesktop,
        CarouselTitleDisplayStyleMobile: CarouselTitleDisplayStyleMobile,
        CarouselTitleDisplayStyleTablet: CarouselTitleDisplayStyleTablet,
        shouldBleedMobile,
        shouldBleedTablet,
        shouldBleedDesktop,
        isAlreadyFullViewport,
        ...props
    })

    // Calculate optimal image sizes based on visible slides
    const imageSizes = useMemo(() => {
        const mobileSlides = Math.floor(TileNumber || 2.5)
        const tabletSlides = Math.floor(TabletTileNumber || 4.5)
        const desktopSlides = Math.floor(DesktopTileNumber || 8)

        return calculateGridImageSizes({
            mobileColumns: mobileSlides,
            tabletColumns: tabletSlides,
            desktopColumns: desktopSlides,
            gap: 8
        })
    }, [TileNumber, TabletTileNumber, DesktopTileNumber])

    const imageWidths = useMemo(() => {
        const mobileSlides = Math.floor(TileNumber || 2.5)
        const tabletSlides = Math.floor(TabletTileNumber || 4.5)
        const desktopSlides = Math.floor(DesktopTileNumber || 8)
        return calculateGridImageWidths(desktopSlides, tabletSlides, mobileSlides)
    }, [TileNumber, TabletTileNumber, DesktopTileNumber])

    // useId gives a deterministic unique value so multiple instances keep independent nav controls
    const carouselId = useId().replace(/:/g, '')
    const prevNavId = `${carouselId}-prev`
    const nextNavId = `${carouselId}-next`

    // Update arrow visibility based on scroll position
    const updateScrollState = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container) return

        const {scrollLeft, scrollWidth, clientWidth} = container
        setCanScrollLeft(scrollLeft > 1)
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
    }, [])

    // Calculate scroll amount for one tile (tile width + gap)
    const getScrollAmount = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container) return 300 // Fallback

        const containerWidth = container.clientWidth
        const slidesToShow = effectiveDesktopSlides
        const gaps = (Math.floor(slidesToShow) - 1) * GAP_SIZE

        // Calculate tile width matching the CSS calc
        // When bleeding: (100% + offset - gaps) / slidesToShow
        // When not bleeding: (100% - gaps) / slidesToShow
        const tileWidth = shouldBleedDesktop
            ? (containerWidth + desktopOffset - gaps) / slidesToShow
            : (containerWidth - gaps) / slidesToShow

        return tileWidth + GAP_SIZE
    }, [effectiveDesktopSlides, shouldBleedDesktop, desktopOffset])

    // Handle arrow clicks for desktop navigation
    const handleScrollLeft = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container) return

        const scrollAmount = getScrollAmount()
        const {scrollLeft} = container

        // If near the start (within one tile), scroll to beginning including offset
        if (scrollLeft < scrollAmount + desktopOffset) {
            container.scrollTo({left: 0, behavior: 'smooth'})
        } else {
            container.scrollBy({left: -scrollAmount, behavior: 'smooth'})
        }
    }, [getScrollAmount, desktopOffset])

    const handleScrollRight = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container) return

        const scrollAmount = getScrollAmount()
        const {scrollLeft, scrollWidth, clientWidth} = container
        const maxScroll = scrollWidth - clientWidth

        // If near the end (within one tile), scroll to the end including offset
        if (scrollLeft > maxScroll - scrollAmount - desktopOffset) {
            container.scrollTo({left: maxScroll, behavior: 'smooth'})
        } else {
            container.scrollBy({left: scrollAmount, behavior: 'smooth'})
        }
    }, [getScrollAmount, desktopOffset])

    // Set up scroll listener for arrow visibility (desktop only)
    useEffect(() => {
        const container = scrollContainerRef.current
        if (!container || typeof window === 'undefined' || !needsDesktopArrows) return

        // Initial check
        updateScrollState()

        // Listen for scroll events
        container.addEventListener('scroll', updateScrollState, {passive: true})
        window.addEventListener('resize', updateScrollState, {passive: true})

        return () => {
            container.removeEventListener('scroll', updateScrollState)
            window.removeEventListener('resize', updateScrollState)
        }
    }, [updateScrollState, needsDesktopArrows])

    const hideStyles = getHideStyles(HideOnDevices)

    if (hideStyles.doNotLoadComponent) return null

    // CSS-only scrollable carousel - works on all viewports
    const renderCSSCarousel = () => (
        <Box
            ref={scrollContainerRef}
            display="flex"
            gap="8px"
            overflowX="auto"
            mx={bleedMargin}
            sx={{
                // Hide scrollbar across browsers
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE/Edge
                '&::-webkit-scrollbar': {
                    display: 'none' // Chrome/Safari/Opera
                },
                // Smooth scrolling on touch devices
                WebkitOverflowScrolling: 'touch',
                // Consistent item sizing - account for padding when bleeding
                '& > *': {
                    flexShrink: 0,
                    width: shouldBleedMobile
                        ? `calc((100% + ${mobileOffset}px - ${(Math.floor(effectiveMobileSlides) - 1) * 8}px) / ${effectiveMobileSlides})`
                        : `calc((100% - ${(Math.floor(effectiveMobileSlides) - 1) * 8}px) / ${effectiveMobileSlides})`
                },
                [`@media (min-width: ${mdBreakpoint}px)`]: {
                    '& > *': {
                        width: shouldBleedTablet
                            ? `calc((100% + ${tabletOffset}px - ${(Math.floor(effectiveTabletSlides) - 1) * 8}px) / ${effectiveTabletSlides})`
                            : `calc((100% - ${(Math.floor(effectiveTabletSlides) - 1) * 8}px) / ${effectiveTabletSlides})`
                    }
                },
                [`@media (min-width: ${lgBreakpoint}px)`]: {
                    '& > *': {
                        width: shouldBleedDesktop
                            ? `calc((100% + ${desktopOffset}px - ${(Math.floor(effectiveDesktopSlides) - 1) * 8}px) / ${effectiveDesktopSlides})`
                            : `calc((100% - ${(Math.floor(effectiveDesktopSlides) - 1) * 8}px) / ${effectiveDesktopSlides})`
                    }
                }
            }}
            // Add padding for bleed effect
            pl={{
                base: shouldBleedMobile ? `${mobileOffset}px` : 0,
                md: shouldBleedTablet ? `${tabletOffset}px` : 0,
                lg: shouldBleedDesktop ? `${desktopOffset}px` : 0
            }}
            pr={{
                base: shouldBleedMobile ? `${mobileOffset}px` : 0,
                md: shouldBleedTablet ? `${tabletOffset}px` : 0,
                lg: shouldBleedDesktop ? `${desktopOffset}px` : 0
            }}
        >
            {Categories?.map((CategoryLinkBlock, index) => (
                <Box key={`CategoryLinkBlock-${index}`}>
                    {renderComponentsSwitch(CategoryLinkBlock?.Expanded, {
                        CtaPlacement: CtaPlacement,
                        TextColor: TextColor,
                        CategoryCarouselSize: CategoryCarouselSize,
                        CategoryCarouselBlockCTATextStyle: CategoryCarouselBlockCTATextStyle,
                        Underline: Underline,
                        widths: imageWidths,
                        sizes: imageSizes,
                        preset: 'TILE'
                    })}
                </Box>
            ))}
        </Box>
    )

    return (
        <Box
            ref={containerRef}
            {...styles.container}
            {...hideStyles}
            data-block-type="CategoryCarouselBlock"
        >
            <Box {...styles.wrapper} {...customWrapperStyling}>
                {!hideTitle && (
                    <Box {...styles.titleWrapper}>
                        <Text
                            {...generateStyles(
                                CategoryCarouselBlockCarouselTitleStyle,
                                {...styles.title},
                                {h2: {base: '20px', lg: '32px'}}
                            )}
                        >
                            {CarouselTitle}
                        </Text>
                    </Box>
                )}
                <Box {...styles.sliderWrapper} position="relative">
                    {renderCSSCarousel()}

                    {/* Desktop arrows - only shown when needed */}
                    {needsDesktopArrows && (
                        <>
                            <ChevronLeftIcon
                                id={prevNavId}
                                display={{base: 'none', lg: canScrollLeft ? 'block' : 'none'}}
                                onClick={handleScrollLeft}
                                cursor="pointer"
                                {...styles.swiperNav}
                                left={shouldBleedDesktop && !isAlreadyFullViewport ? '-24px' : 0}
                            />
                            <ChevronRightIcon
                                id={nextNavId}
                                display={{base: 'none', lg: canScrollRight ? 'block' : 'none'}}
                                onClick={handleScrollRight}
                                cursor="pointer"
                                {...styles.swiperNav}
                                right={shouldBleedDesktop && !isAlreadyFullViewport ? '-24px' : 0}
                            />
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

CategoryCarouselBlock.propTypes = {
    Categories: PropTypes.array,
    CarouselTitle: PropTypes.string,
    CarouselTitleDisplayStyleDesktop: PropTypes.object,
    CarouselTitleDisplayStyleMobile: PropTypes.object,
    CarouselTitleDisplayStyleTablet: PropTypes.object,
    CategoryCarouselBlockCTATextStyle: PropTypes.object,
    CategoryCarouselBlockCarouselTitleStyle: PropTypes.object,
    hideTitle: PropTypes.bool,
    customWrapperStyling: PropTypes.object,
    CtaPlacement: PropTypes.string,
    CategoryCarouselSize: PropTypes.string,
    DesktopTileNumber: PropTypes.number,
    TabletTileNumber: PropTypes.number,
    TileNumber: PropTypes.number,
    TextColor: PropTypes.string,
    Underline: PropTypes.bool,
    HideOnDevices: PropTypes.array,
    hydrationCount: PropTypes.number
}

export default CategoryCarouselBlock
